import type { MotifId } from '../core/types';

/**
 * Generative WebAudio synthesizer for Isidora:
 * - Bowed & plucked Cremonese violin harmonics (rich sawtooth/triangle + body formant filter)
 * - Seashell cavity resonant bandpass wind & pearlescent chimes
 * - Observatory glass celestial harmonic
 * - Continuous ambient twilight drone morphing between Desire (warm D Dorian fifths)
 *   and Memory (high silver harmonics)
 */
export class IsidoraSoundscape {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private droneGain: GainNode | null = null;
  private droneFilter: BiquadFilterNode | null = null;
  private droneOscA: OscillatorNode | null = null;
  private droneOscB: OscillatorNode | null = null;
  private enabled = false;
  private temporal = 0.15;

  public toggle(): boolean {
    if (!this.enabled) {
      this.enable();
    } else {
      this.disable();
    }
    return this.enabled;
  }

  public isEnabled(): boolean {
    return this.enabled;
  }

  public enable(): void {
    if (!this.ctx) {
      this.ctx = new AudioContext();

      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = 0.35;
      this.masterGain.connect(this.ctx.destination);

      // Continuous subtle ambient drone (D2 + A2 fifth)
      this.droneFilter = this.ctx.createBiquadFilter();
      this.droneFilter.type = 'lowpass';
      this.droneFilter.frequency.value = 340;

      this.droneGain = this.ctx.createGain();
      this.droneGain.gain.value = 0.08;

      this.droneOscA = this.ctx.createOscillator();
      this.droneOscA.type = 'sine';
      this.droneOscA.frequency.value = 73.42; // D2

      this.droneOscB = this.ctx.createOscillator();
      this.droneOscB.type = 'triangle';
      this.droneOscB.frequency.value = 110.0; // A2

      this.droneOscA.connect(this.droneFilter);
      this.droneOscB.connect(this.droneFilter);
      this.droneFilter.connect(this.droneGain);
      this.droneGain.connect(this.masterGain);

      this.droneOscA.start();
      this.droneOscB.start();
    }

    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
    if (this.masterGain && this.ctx) {
      this.masterGain.gain.setTargetAtTime(0.35, this.ctx.currentTime, 0.25);
    }
    this.enabled = true;
  }

  public disable(): void {
    if (this.masterGain && this.ctx) {
      this.masterGain.gain.setTargetAtTime(0.0001, this.ctx.currentTime, 0.2);
    }
    this.enabled = false;
  }

  public setTemporalState(temporal: number): void {
    this.temporal = temporal;
    if (!this.ctx || !this.droneFilter || !this.droneOscB) return;
    const now = this.ctx.currentTime;
    const cutoff = 380 - temporal * 190;
    this.droneFilter.frequency.setTargetAtTime(cutoff, now, 0.3);
  }

  /**
   * Synthesizes a plucked & softly bowed Cremonese violin note with wooden body resonance.
   */
  public playViolinString(freq = 293.66): void {
    if (!this.enabled || !this.ctx || !this.masterGain) return;
    const now = this.ctx.currentTime;

    const osc1 = this.ctx.createOscillator();
    const osc2 = this.ctx.createOscillator();
    osc1.type = 'sawtooth';
    osc2.type = 'triangle';
    osc1.frequency.setValueAtTime(freq, now);
    osc2.frequency.setValueAtTime(freq * 2.0, now);

    const formant = this.ctx.createBiquadFilter();
    formant.type = 'bandpass';
    formant.frequency.setValueAtTime(Math.min(2400, freq * 2.4), now);
    formant.Q.setValueAtTime(2.6, now);

    const env = this.ctx.createGain();
    env.gain.setValueAtTime(0.001, now);
    env.gain.linearRampToValueAtTime(0.28, now + 0.045);
    env.gain.exponentialRampToValueAtTime(0.08, now + 0.55);
    env.gain.exponentialRampToValueAtTime(0.0008, now + 2.6);

    osc1.connect(formant);
    osc2.connect(formant);
    formant.connect(env);
    env.connect(this.masterGain);

    osc1.start(now);
    osc2.start(now);
    osc1.stop(now + 2.65);
    osc2.stop(now + 2.65);
  }

  /**
   * Synthesizes a spiral seashell cavity resonance + nacre shimmer harmonic.
   */
  public playSeashellResonance(freq = 329.63): void {
    if (!this.enabled || !this.ctx || !this.masterGain) return;
    const now = this.ctx.currentTime;

    for (const ratio of [1.0, 1.498, 2.0]) {
      const osc = this.ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq * ratio, now);

      const gain = this.ctx.createGain();
      gain.gain.setValueAtTime(0.001, now);
      gain.gain.linearRampToValueAtTime(0.11 / ratio, now + 0.18);
      gain.gain.exponentialRampToValueAtTime(0.0008, now + 2.8);

      osc.connect(gain);
      gain.connect(this.masterGain);
      osc.start(now);
      osc.stop(now + 2.85);
    }
  }

  public playMotifCue(motif: MotifId, freq?: number): void {
    if (!this.enabled) return;
    if (motif === 'violins') {
      this.playViolinString(freq ?? 440.0);
    } else if (motif === 'seashells' || motif === 'spirals') {
      this.playSeashellResonance(freq ?? 293.66);
    } else if (motif === 'telescopes') {
      this.playSeashellResonance(freq ?? 587.33);
    } else if (motif === 'wall') {
      this.playViolinString(220.0);
    } else {
      this.playSeashellResonance(261.63);
    }
  }
}
